import Resources from '@/Resources.js';

import {
    projectPhaseSearchType,
    projectSearchPlayerAge,
    projectBenefitTypes,
    projectSearchPlayerCount,
    playTimeSearchType,
    userCommunitySearchType,
} from '@/Models';

export const filterProjectSearchTypes = {
    projectPhase: 0,
    tag: 1,
    creator: 2,
    searchTerm: 3,
    playerAges: 4,
    playerCounts: 5,
    playTimes: 6,
    projectContribution: 7,
    userCommunity: 8,
    projectBenefit: 9,
    catalogCategory: 10,
};

export function getFilterGroupDisplayName(filterSearchType) {
    const filterGroupDisplayNames = {
        0: Resources.Search.SearchFiltersGroupStatusLabel,
        1: Resources.Search.SearchFiltersGroupTagLabel,
        2: Resources.Search.SearchFiltersGroupCreatorLabel,
        3: Resources.Search.SearchFiltersGroupSearchPhraseLabel,
        4: Resources.Search.SearchFiltersGroupAgeLabel,
        5: Resources.Search.SearchFiltersGroupPlayableLabel,
        6: Resources.Search.SearchFiltersGroupPlaytimeLabel,
        7: Resources.Search.SearchFiltersGroupContributionLabel,
        8: Resources.Search.SearchFiltersGroupCommunityLabel,
        9: Resources.Search.SearchProjectsFiltersBenefitLabel,
        10: Resources.Search.SearchProjectsFiltersCategoryLabel,
    };
    return filterGroupDisplayNames[filterSearchType];
}

export const pushFilterFromEnum = (filters, obj, filterSearchType) => (value) => {
    filters.push({
        key: obj[value].name,
        label: obj[value].displayName,
        group: getFilterGroupDisplayName(filterSearchType),
        filterSearchType,
        value,
    });
};

export function buildDefaultSearchFilterParams(params = {}) {
    return {
        creatorID: params.creatorID || null,
        creatorName: params.creatorName || null,
        sortType: params.sortType || 0,
        term: params.term || '',
        projectPhaseSearchTypes: params.projectPhaseSearchTypes || [],
        projectBenefits: params.projectBenefits || [],
        projectTags: params.projectTags || [],
        projectCatalogCategories: params.projectCatalogCategories || [],
        playerAges: params.playerAges || [],
        playerCounts: params.playerCounts || [],
        playTimes: params.playTimes || [],
        creator: {
            creatorID: params.creatorID || null,
            name: params.creatorName || null,
        },
    };
}

export function buildProjectSearchFilterParams(params = {}) {
    const result = buildDefaultSearchFilterParams(params);
    result.userCommunitySearchTypes = params.userCommunitySearchTypes || [];
    result.source = params.source;
    return result;
}

export function buildUserProjectSearchFilterParams(params = {}) {
    const result = buildDefaultSearchFilterParams(params);
    result.projectContributionTypes = params.projectContributionTypes || [];
    return result;
}

export function buildProjectSearchCurrentFilter(searchParams, projectTags, categories) {
    const filters = [];

    if (searchParams?.projectPhaseSearchTypes?.length > 0) {
        searchParams.projectPhaseSearchTypes
            .forEach(pushFilterFromEnum(filters, projectPhaseSearchType, filterProjectSearchTypes.projectPhase));
    }

    if (searchParams.projectBenefits?.length > 0) {
        searchParams.projectBenefits
            .forEach(pushFilterFromEnum(filters, projectBenefitTypes, filterProjectSearchTypes.projectBenefit));
    }

    if (searchParams.projectTags?.length > 0) {
        searchParams.projectTags
            .forEach(v => {
                const tag = projectTags
                    .find(c => c.projectTagID === v);
                if (tag) {
                    filters.push({
                        key: tag.urlName,
                        label: tag.name,
                        group: getFilterGroupDisplayName(filterProjectSearchTypes.tag),
                        filterSearchType: filterProjectSearchTypes.tag,
                        value: tag.projectTagID,
                    });
                }
            });
    }

    if (searchParams.projectCatalogCategories?.length > 0) {
        searchParams.projectCatalogCategories.forEach(v => {
            const all = categories.flatMap(c => [
                c,
                ...Array.isArray(c.subCategories) ? c.subCategories : [],
            ]);
            const category = all.find(c => c.categoryID === v);

            if (category) {
                filters.push({
                    key: category.categoryID,
                    label: category.name,
                    group: getFilterGroupDisplayName(filterProjectSearchTypes.catalogCategory),
                    filterSearchType: filterProjectSearchTypes.catalogCategory,
                    value: category.categoryID,
                });
            }
        });
    }
    if (searchParams.userCommunitySearchTypes?.length > 0) {
        searchParams.userCommunitySearchTypes
            .forEach(pushFilterFromEnum(
                filters,
                userCommunitySearchType,
                filterProjectSearchTypes.userCommunity,
            ));
    }

    if (searchParams.playerAges?.length > 0) {
        searchParams.playerAges
            .forEach(pushFilterFromEnum(
                filters,
                projectSearchPlayerAge,
                filterProjectSearchTypes.playerAges,
            ));
    }

    if (searchParams.playerCounts?.length > 0) {
        searchParams.playerCounts
            .forEach(pushFilterFromEnum(
                filters,
                projectSearchPlayerCount,
                filterProjectSearchTypes.playerCounts,
            ));
    }

    if (searchParams.playTimes?.length > 0) {
        searchParams.playTimes
            .forEach(pushFilterFromEnum(
                filters,
                playTimeSearchType,
                filterProjectSearchTypes.playTimes,
            ));
    }

    if (searchParams.creatorID) {
        filters.push({
            key: 'creator',
            group: getFilterGroupDisplayName(filterProjectSearchTypes.creator),
            label: searchParams.creatorName,
            filterSearchType: filterProjectSearchTypes.creator,
        });
    }

    if (searchParams.term) {
        filters.push({
            key: 'searchTerm',
            group: getFilterGroupDisplayName(filterProjectSearchTypes.searchTerm),
            label: searchParams.term,
            filterSearchType: filterProjectSearchTypes.searchTerm,
        });
    }

    return filters;
}

export function removeProjectSearchSelectedFilter(filter, searchParams) {
    const result = searchParams;
    const filterValue = v => v !== filter.value;

    if (filter.filterSearchType === filterProjectSearchTypes.projectPhase) {
        result.projectPhaseSearchTypes
            = searchParams.projectPhaseSearchTypes.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.projectContribution) {
        result.projectContributionTypes
            = searchParams.projectContributionTypes.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.tag) {
        result.projectTags
            = searchParams.projectTags.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.playerAges) {
        result.playerAges
            = searchParams.playerAges.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.playerCounts) {
        result.playerCounts
            = searchParams.playerCounts.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.playTimes) {
        result.playTimes
            = searchParams.playTimes.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.creator) {
        result.creator = null;
        result.creatorID = null;
        result.creatorName = null;
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.searchTerm) {
        result.term = null;
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.userCommunity) {
        result.userCommunitySearchTypes
            = searchParams.userCommunitySearchTypes.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.projectBenefit) {
        result.projectBenefits
            = searchParams.projectBenefits.filter(filterValue);
    }
    else if (filter.filterSearchType === filterProjectSearchTypes.catalogCategory) {
        result.projectCatalogCategories
            = searchParams.projectCatalogCategories.filter(filterValue);
    }

    return result;
}